import React, { useState, useEffect } from 'react';
import AddStreamModal from './AddStreamModal';
import ViewStreamModal from './ViewStreamModal';
import '../styles/StreamManagement.css';

const StreamManagement = () => {
  const [streams, setStreams] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [editingStream, setEditingStream] = useState(null);
  const [viewStream, setViewStream] = useState(null);

  useEffect(() => {
    fetchStreams();
  }, []);

  const fetchStreams = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/streams/all');
      const data = await response.json();
      if (response.ok) {
        setStreams(data.streams);
      } else {
        setError(data.message || 'Failed to fetch streams');
      }
    } catch (err) {
      setError('Error loading streams: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStream = (newStream) => {
    setStreams([...streams, newStream]);
    setIsModalOpen(false);
  };

  const handleEditStream = (stream) => {
    setEditingStream(stream);
    setIsModalOpen(true);
  };

  const handleUpdateStream = (updatedStream) => {
    setStreams(streams.map(s => s.id === updatedStream.id ? updatedStream : s));
    setEditingStream(null);
    setIsModalOpen(false);
  };

  const handleDeleteStream = async (streamId) => {
    if (!window.confirm('Are you sure you want to delete this stream?')) return;

    try {
      const response = await fetch(`/api/streams/${streamId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (response.ok) {
        setStreams(streams.filter(s => s.id !== streamId));
      } else {
        setError(data.message || 'Failed to delete stream');
      }
    } catch (err) {
      setError('Error deleting stream: ' + err.message);
    }
  };

  const handleToggleActive = async (streamId, currentStatus) => {
    try {
      const response = await fetch(`/api/streams/${streamId}/toggle-active`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      const data = await response.json();

      if (response.ok) {
        setStreams(streams.map(s =>
          s.id === streamId ? { ...s, is_active: !currentStatus } : s
        ));
      } else {
        setError(data.message || 'Failed to update stream');
      }
    } catch (err) {
      setError('Error updating stream: ' + err.message);
    }
  };

  const handleTogglePublic = async (streamId, currentStatus) => {
    try {
      const response = await fetch(`/api/streams/${streamId}/toggle-public`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_public: !currentStatus })
      });
      const data = await response.json();

      if (response.ok) {
        setStreams(streams.map(s =>
          s.id === streamId ? { ...s, is_public: !currentStatus } : s
        ));
      } else {
        setError(data.message || 'Failed to update stream');
      }
    } catch (err) {
      setError('Error updating stream: ' + err.message);
    }
  };

  const filteredStreams = streams.filter(stream => {
    const matchesSearch = stream.stream_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                stream.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || stream.stream_type === filterType;
    // Show all streams, regardless of content columns
    return matchesSearch && matchesFilter;
  });

  const getStreamIcon = (type) => {
    const icons = {
      video: '📹',
      audio: '🎵',
      embed: '🔗',
      live: '🔴',
      premium: '👑'
    };
    return icons[type] || '📺';
  };

  const getStreamTypeLabel = (type) => {
    const labels = {
      video: 'Video',
      audio: 'Audio',
      embed: 'Embed',
      live: 'Live',
      premium: 'Premium'
    };
    return labels[type] || type;
  };

  return (
    <div className="stream-management">
      <div className="management-header">
        <div>
          <h1>Stream Management</h1>
          <p>Manage all your streams, videos, and audio content</p>
        </div>
        <button
          className="btn-primary btn-lg"
          onClick={() => setIsModalOpen(true)}
        >
          ➕ Add New Stream
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')}>&times;</button>
        </div>
      )}

      <div className="management-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search streams by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="search-icon">🔍</span>
        </div>

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Types</option>
          <option value="video">📹 Videos</option>
          <option value="audio">🎵 Audio</option>
          <option value="embed">🔗 Embeds</option>
          <option value="live">🔴 Live</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">Loading streams...</div>
      ) : filteredStreams.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📺</div>
          <h2>No Streams Found</h2>
          <p>{streams.length === 0 ? 'Create your first stream to get started!' : 'No streams match your filters.'}</p>
          <button className="btn-primary" onClick={() => setIsModalOpen(true)}>
            Create First Stream
          </button>
        </div>
      ) : (
        <div className="streams-grid">
          {filteredStreams.map(stream => (
            <div key={stream.id} className={`stream-card ${stream.is_active ? 'active' : 'inactive'}`}
              onClick={() => setViewStream(stream)}
              style={{ cursor: 'pointer' }}
            >
              <div className="stream-header">
                <div className="stream-title">
                  <span className="stream-icon">{getStreamIcon(stream.stream_type)}</span>
                  <div>
                    <h3>{stream.stream_name}</h3>
                    <span className="stream-type">{getStreamTypeLabel(stream.stream_type)}</span>
                  </div>
                </div>
                <div className="stream-actions">
                  <button
                    className="icon-btn"
                    title={stream.is_active ? 'Deactivate' : 'Activate'}
                    onClick={() => handleToggleActive(stream.id, stream.is_active)}
                  >
                    {stream.is_active ? '✅' : '⭕'}
                  </button>
                  <button
                    className="icon-btn"
                    title={stream.is_public ? 'Make Private' : 'Make Public'}
                    onClick={() => handleTogglePublic(stream.id, stream.is_public)}
                  >
                    {stream.is_public ? '🌐' : '🔒'}
                  </button>
                  <button
                    className="icon-btn"
                    title="Edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditStream(stream);
                    }}
                  >
                    ✏️
                  </button>
                  <button
                    className="icon-btn delete"
                    title="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteStream(stream.id);
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <p className="stream-description">{stream.description}</p>

              <div className="stream-meta">
                {stream.stream_video_path && (
                  <span className="meta-item">
                    📂 {stream.stream_video_path.split('/').pop()}
                  </span>
                )}
                {stream.stream_video_link && (
                  <span className="meta-item">
                    🔗 External Link
                  </span>
                )}
              </div>

              <div className="stream-footer">
                <div className="status-badges">
                  {stream.is_active ? (
                    <span className="badge badge-active">Active</span>
                  ) : (
                    <span className="badge badge-inactive">Inactive</span>
                  )}
                  {stream.is_public ? (
                    <span className="badge badge-public">Public</span>
                  ) : (
                    <span className="badge badge-private">Private</span>
                  )}
                </div>
                <small className="created-date">
                  Created: {new Date(stream.created_at).toLocaleDateString()}
                </small>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddStreamModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingStream(null);
        }}
        onAdd={handleAddStream}
        onSave={handleUpdateStream}
        editingStream={editingStream}
      />
      <ViewStreamModal
        isOpen={!!viewStream}
        onClose={() => setViewStream(null)}
        stream={viewStream}
      />
    </div>
  );
};

export default StreamManagement;
